/**
 * Frank Karsten 2022 Multi-Stage Source & Land Allocation Engine
 * Supports:
 * - Scryfall format legality & color identity constraints
 * - Non-land mana cost & activated/utility ability pip counting
 * - Karsten 2022 Turn/Pip cost floors (T1 {C}: 14, T2 {CC}: 21, T3 {CCC}: 23, T4 {CCCC}: 25)
 * - Dork/rock offsets & cantrip/scry/surveil/explore modifiers
 * - Tapland vs Fastland/Pathway/Shockland T1 vs T2+ timing rules
 * - Multi-color land consolidation (Stomping Ground = 2 sources, Starting Town = N sources, Fabled Passage)
 * - 100% dynamic context-only advice generator
 */

import { Card } from '../App';

export interface PipCountResult {
  pips: Record<string, number>;
  totalPips: number;
  ratios: Record<string, number>;
  heaviestPipSpells: Record<string, { cardName: string; cost: string; cmc: number; requiredSources: number }>;
  t1OneDrops: Record<string, string[]>; // Color -> Card names requiring T1 untapped source
  cantripCount: number;
  cantripNames: string[];
  dorkRockSources: Record<string, number>; // Color -> dork/rock sources
}

export interface SuggestedLandItem {
  name: string;
  quantity: number;
  type: string;
  colors: string[];
  imageUrl: string;
  isBasic: boolean;
  isDual: boolean;
  isRainbow: boolean;
  isFetch: boolean;
}

export interface AutoSuggestResult {
  targetLands: number;
  adjustedTargetLands: number;
  suggestedLands: SuggestedLandItem[];
  sourcesNeeded: Record<string, number>;
  sourcesProvided: Record<string, number>;
  pipStats: PipCountResult;
  advice: string[];
}

// Scryfall image fallback mapping for basic and standard utility lands
const LAND_IMAGES: Record<string, string> = {
  'Forest': 'https://cards.scryfall.io/normal/front/1/7/17b2b629-87a3-41a4-9d54-20d099951b14.jpg',
  'Mountain': 'https://cards.scryfall.io/normal/front/a/e/ae5617a2-f851-4e78-9580-f00e998782a2.jpg',
  'Swamp': 'https://cards.scryfall.io/normal/front/9/9/99a4c8f2-d6ff-4ecf-9154-e0327f274fb8.jpg',
  'Island': 'https://cards.scryfall.io/normal/front/f/f/ff3b34f6-80f4-4e3e-bc54-0354ed1a6977.jpg',
  'Plains': 'https://cards.scryfall.io/normal/front/7/8/786a51d4-8395-4674-8848-0d12e69faaa8.jpg',
  'Stomping Ground': 'https://cards.scryfall.io/normal/front/d/e/d87041ad-0363-4fd8-ba25-9636785441ac.jpg',
  'Overgrown Tomb': 'https://cards.scryfall.io/normal/front/e/f/efac83d0-b995-4d7a-8f06-e7f781a7b055.jpg',
  'Temple Garden': 'https://cards.scryfall.io/normal/front/2/b/2b9b0195-bdda-4737-82c6-fb1d1757389b.jpg',
  'Sacred Foundry': 'https://cards.scryfall.io/normal/front/b/7/b75a408a-c51e-487b-a19c-85153288124b.jpg',
  'Breeding Pool': 'https://cards.scryfall.io/normal/front/b/b/bb54233c-0844-4965-9cde-e8a4ef3e11b8.jpg',
  'Steam Vents': 'https://cards.scryfall.io/normal/front/b/2/b251c019-58d6-44f0-8888-80cb1ad1e576.jpg',
  'Watery Grave': 'https://cards.scryfall.io/normal/front/7/d/7d459580-0d3d-409a-b6fe-73516d338276.jpg',
  'Blood Crypt': 'https://cards.scryfall.io/normal/front/5/4/5472a715-bed8-4531-a304-4094733176f7.jpg',
  'Godless Shrine': 'https://cards.scryfall.io/normal/front/c/e/ce82b16c-f786-4cf1-b89e-46ee4656114b.jpg',
  'Hallowed Fountain': 'https://cards.scryfall.io/normal/front/f/9/f97a6d34-03ab-49f1-b02e-405b733f8843.jpg',
  'Starting Town': 'https://cards.scryfall.io/normal/front/e/f/efac83d0-b995-4d7a-8f06-e7f781a7b055.jpg',
  'Multiversal Passage': 'https://cards.scryfall.io/normal/front/a/e/ae5617a2-f851-4e78-9580-f00e998782a2.jpg',
  'Fabled Passage': 'https://cards.scryfall.io/normal/front/d/3/d313d051-7295-4884-8cbf-f2f835fd45f4.jpg',
  'Command Tower': 'https://cards.scryfall.io/normal/front/0/5/057a19e6-27e1-4802-965a-02c01824754b.jpg',
};

// Map of formats to legal non-basic dual/rainbow/fetch lands
interface LandCatalogItem {
  name: string;
  type: string;
  colors: string[];
  formats: string[]; // Legal formats
  isDual: boolean;
  isRainbow: boolean;
  isFetch: boolean;
  entersTapped: boolean;
}

const LAND_CATALOG: LandCatalogItem[] = [
  // Rainbow Lands
  { name: 'Starting Town', type: 'Land', colors: ['W', 'U', 'B', 'R', 'G'], formats: ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: false, isRainbow: true, isFetch: false, entersTapped: true },
  { name: 'Multiversal Passage', type: 'Land', colors: ['W', 'U', 'B', 'R', 'G'], formats: ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: false, isRainbow: true, isFetch: false, entersTapped: false },
  { name: 'Command Tower', type: 'Land', colors: ['W', 'U', 'B', 'R', 'G'], formats: ['commander', 'legacy', 'vintage'], isDual: false, isRainbow: true, isFetch: false, entersTapped: false },
  { name: 'Fabled Passage', type: 'Land', colors: ['W', 'U', 'B', 'R', 'G'], formats: ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: false, isRainbow: false, isFetch: true, entersTapped: false },

  // Standard Duals (Shocklands & Painlands & Fastlands)
  { name: 'Stomping Ground', type: 'Land — Mountain Forest', colors: ['R', 'G'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Overgrown Tomb', type: 'Land — Swamp Forest', colors: ['B', 'G'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Temple Garden', type: 'Land — Forest Plains', colors: ['G', 'W'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Sacred Foundry', type: 'Land — Mountain Plains', colors: ['R', 'W'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Breeding Pool', type: 'Land — Forest Island', colors: ['G', 'U'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Steam Vents', type: 'Land — Island Mountain', colors: ['U', 'R'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Watery Grave', type: 'Land — Island Swamp', colors: ['U', 'B'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Blood Crypt', type: 'Land — Swamp Mountain', colors: ['B', 'R'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Godless Shrine', type: 'Land — Plains Swamp', colors: ['W', 'B'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Hallowed Fountain', type: 'Land — Plains Island', colors: ['W', 'U'], formats: ['pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },

  // Fastlands (Copperline Gorge, etc.)
  { name: 'Copperline Gorge', type: 'Land', colors: ['R', 'G'], formats: ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Blackcleave Cliffs', type: 'Land', colors: ['B', 'R'], formats: ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Razorverge Thicket', type: 'Land', colors: ['G', 'W'], formats: ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Seachrome Coast', type: 'Land', colors: ['W', 'U'], formats: ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Darkslick Shores', type: 'Land', colors: ['U', 'B'], formats: ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
  { name: 'Spirebluff Canal', type: 'Land', colors: ['U', 'R'], formats: ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander'], isDual: true, isRainbow: false, isFetch: false, entersTapped: false },
];

/**
 * Parses all pips in mana costs AND activated/utility abilities in oracle text.
 */
export function calculateDeckPipCounts(deck: Card[]): PipCountResult {
  const pips: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };
  const heaviestPipSpells: Record<string, { cardName: string; cost: string; cmc: number; requiredSources: number }> = {};
  const t1OneDrops: Record<string, string[]> = { W: [], U: [], B: [], R: [], G: [] };
  let cantripCount = 0;
  const cantripNames: string[] = [];
  const dorkRockSources: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };

  const colors = ['W', 'U', 'B', 'R', 'G'] as const;

  for (const card of deck) {
    if (card.isLand) continue;
    const qty = card.quantity || 1;

    // 1. Mana Cost Pips
    if (card.manaCost) {
      for (const color of colors) {
        const regex = new RegExp(`\\{${color}\\}`, 'g');
        const matches = (card.manaCost.match(regex) || []).length;
        if (matches > 0) {
          pips[color] += matches * qty;

          // Check heavy pip single-card requirements (Frank Karsten 2022 Table)
          const cmc = card.cmc || 0;
          let required = 13; // default turn 2 1C baseline

          if (cmc === 1 && matches >= 1) {
            required = 14; // Turn 1 {C}
            t1OneDrops[color].push(card.name);
          } else if (cmc === 2 && matches === 2) {
            required = 21; // Turn 2 {CC}
          } else if (cmc === 3 && matches === 2) {
            required = 18; // Turn 3 {1CC}
          } else if (cmc === 3 && matches >= 3) {
            required = 23; // Turn 3 {CCC}
          } else if (cmc === 4 && matches === 2) {
            required = 15; // Turn 4 {2CC}
          } else if (cmc === 4 && matches === 3) {
            required = 22; // Turn 4 {1CCC}
          } else if (cmc === 4 && matches >= 4) {
            required = 25; // Turn 4 {CCCC}
          } else if (matches >= 1) {
            required = Math.max(12, 14 - Math.floor(cmc / 2));
          }

          if (!heaviestPipSpells[color] || required > heaviestPipSpells[color].requiredSources) {
            heaviestPipSpells[color] = {
              cardName: card.name,
              cost: card.manaCost,
              cmc,
              requiredSources: required,
            };
          }
        }
      }
    }

    // 2. Oracle Text Activated Ability Pip Costs (ONLY symbols before a colon ':')
    if (card.oracleText) {
      const lines = card.oracleText.split('\n');
      for (const line of lines) {
        if (line.includes(':')) {
          const costPart = line.split(':')[0];
          const abilityRegex = /\{([WUBRGC\d\/]+)\}/g;
          let match;
          while ((match = abilityRegex.exec(costPart)) !== null) {
            const symbol = match[1];
            for (const color of colors) {
              if (symbol.includes(color)) {
                pips[color] += 1 * qty;
              }
            }
          }
        }
      }

      // Check cantrip / card selection (Opt, Consider, Scry, Surveil, Explore)
      const lowerText = card.oracleText.toLowerCase();
      const isOpponentEffect = lowerText.includes('its controller creates') || lowerText.includes('target opponent') || lowerText.includes('an opponent');
      const isCantrip =
        card.cmc <= 2 &&
        !isOpponentEffect &&
        (lowerText.includes('scry') ||
          lowerText.includes('surveil') ||
          lowerText.includes('explore') ||
          lowerText.includes('draw a card') ||
          ['opt', 'consider', 'ponder', 'preordain', 'brainstorm', 'explore', 'sleight of hand', 'gitaxian probe', 'impulse', 'expressive iteration', 'abundant growth'].includes(card.name.toLowerCase()));

      if (isCantrip) {
        cantripCount += qty;
        if (!cantripNames.includes(card.name)) {
          cantripNames.push(card.name);
        }
      }

      // Check Mana Dorks / Rocks
      if (lowerText.includes('add {') || lowerText.includes('add one mana')) {
        for (const color of colors) {
          if (lowerText.includes(`{${color.toLowerCase()}}`) || lowerText.includes('any color')) {
            dorkRockSources[color] += qty;
          }
        }
      }
    }
  }

  const totalPips = Object.values(pips).reduce((a, b) => a + b, 0);
  const ratios: Record<string, number> = {};
  for (const color of colors) {
    ratios[color] = totalPips > 0 ? pips[color] / totalPips : 0;
  }

  return {
    pips,
    totalPips,
    ratios,
    heaviestPipSpells,
    t1OneDrops,
    cantripCount,
    cantripNames,
    dorkRockSources,
  };
}

/**
 * Calculates missing basic lands or rebalances basic lands to hit target land count & color source ratios
 */
export function generateMissingLandSuggestions(
  deck: Card[],
  targetLands: number = 24,
  format: string = 'standard'
): AutoSuggestResult {
  const currentLandCards = deck.filter(c => c.isLand);
  const currentLandCount = currentLandCards.reduce((sum, c) => sum + c.quantity, 0);

  const pipStats = calculateDeckPipCounts(deck);
  const colors = ['W', 'U', 'B', 'R', 'G'] as const;
  const activeColors = colors.filter(c => pipStats.pips[c] > 0);

  const sourcesNeeded: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };
  for (const c of activeColors) {
    const heavyRequirement = pipStats.heaviestPipSpells[c]?.requiredSources || 13;
    const dorkOffset = Math.min(2, pipStats.dorkRockSources[c] || 0);
    const cantripVirtualSource = Math.floor(pipStats.cantripCount / 3);
    sourcesNeeded[c] = Math.max(8, heavyRequirement - dorkOffset - cantripVirtualSource);
  }

  const actualProvided = calculateActualLandSources(deck, activeColors);

  // If no active colors in deck
  if (activeColors.length === 0) {
    const advice = generateDynamicManaAdvice(deck, [], sourcesNeeded, actualProvided, pipStats, targetLands, format);
    return {
      targetLands,
      adjustedTargetLands: targetLands,
      suggestedLands: [],
      sourcesNeeded,
      sourcesProvided: actualProvided,
      pipStats,
      advice,
    };
  }

  // If deck has 0 lands, run full land suggestion engine
  if (currentLandCount === 0) {
    return generateLandSuggestions(deck, targetLands, format);
  }

  const basicNameMap: Record<string, { name: string; type: string }> = {
    W: { name: 'Plains', type: 'Basic Land — Plains' },
    U: { name: 'Island', type: 'Basic Land — Island' },
    B: { name: 'Swamp', type: 'Basic Land — Swamp' },
    R: { name: 'Mountain', type: 'Basic Land — Mountain' },
    G: { name: 'Forest', type: 'Basic Land — Forest' },
  };

  const suggestedLands: SuggestedLandItem[] = [];

  // Count non-basic land slots (duals, fetches, utility lands)
  const basicNames = ['plains', 'island', 'swamp', 'mountain', 'forest'];
  const nonBasicLands = currentLandCards.filter(c => !basicNames.includes(c.name.toLowerCase()));
  const nonBasicCount = nonBasicLands.reduce((sum, c) => sum + c.quantity, 0);

  // Calculate budget available for basic lands
  const basicBudget = Math.max(0, targetLands - nonBasicCount);

  // Deficits per color
  const deficits: Record<string, number> = {};
  for (const c of activeColors) {
    deficits[c] = Math.max(0, sourcesNeeded[c] - (actualProvided[c] || 0));
  }
  const totalDeficit = Object.values(deficits).reduce((a, b) => a + b, 0);

  // Determine color weights for basic lands based on deficits or pip ratios
  const activePipsTotal = activeColors.reduce((sum, c) => sum + pipStats.pips[c], 0);
  const weights: Record<string, number> = {};
  for (const c of activeColors) {
    if (totalDeficit > 0 && deficits[c] > 0) {
      weights[c] = deficits[c] / totalDeficit;
    } else {
      weights[c] = activePipsTotal > 0 ? pipStats.pips[c] / activePipsTotal : 1 / activeColors.length;
    }
  }

  let allocated = 0;
  activeColors.forEach((c, idx) => {
    let qty: number;
    if (idx === activeColors.length - 1) {
      qty = basicBudget - allocated;
    } else {
      qty = Math.round(basicBudget * weights[c]);
    }
    qty = Math.max(0, qty);

    if (qty > 0) {
      const basicInfo = basicNameMap[c];
      suggestedLands.push({
        name: basicInfo.name,
        quantity: qty,
        type: basicInfo.type,
        colors: [c],
        imageUrl: LAND_IMAGES[basicInfo.name] || LAND_IMAGES['Forest'],
        isBasic: true,
        isDual: false,
        isRainbow: false,
        isFetch: false,
      });
      allocated += qty;
    }
  });

  const advice = generateDynamicManaAdvice(deck, suggestedLands, sourcesNeeded, actualProvided, pipStats, targetLands, format);

  return {
    targetLands,
    adjustedTargetLands: targetLands,
    suggestedLands,
    sourcesNeeded,
    sourcesProvided: actualProvided,
    pipStats,
    advice,
  };
}

/**
 * Main Frank Karsten Land Suggestion & Multi-Color Allocation Engine
 */
export function generateLandSuggestions(
  deck: Card[],
  targetLands: number = 24,
  format: string = 'standard'
): AutoSuggestResult {
  const currentLandCount = deck.filter(c => c.isLand).reduce((sum, c) => sum + c.quantity, 0);

  // If deck already has land cards, use missing land calculation to finish the deck
  if (currentLandCount > 0) {
    return generateMissingLandSuggestions(deck, targetLands, format);
  }

  const pipStats = calculateDeckPipCounts(deck);
  const colors = ['W', 'U', 'B', 'R', 'G'] as const;

  // Active colors in deck (colors with > 0 pips)
  const activeColors = colors.filter(c => pipStats.pips[c] > 0);

  // 1. Calculate Cantrip Discount (-1 land per 3-4 cantrips, max -2 lands)
  const cantripDiscount = Math.min(2, Math.floor(pipStats.cantripCount / 3.5));
  const adjustedTargetLands = Math.max(16, targetLands - cantripDiscount);

  // 2. Compute Karsten Source Floors (S_C) per active color
  const sourcesNeeded: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };
  for (const c of activeColors) {
    const heavyRequirement = pipStats.heaviestPipSpells[c]?.requiredSources || 13;
    // Dork offset: dorks/rocks reduce required land sources for turn 2+ by up to 2
    const dorkOffset = Math.min(2, pipStats.dorkRockSources[c] || 0);
    // Cantrip virtual source: +1 virtual source per 3 cantrips
    const cantripVirtualSource = Math.floor(pipStats.cantripCount / 3);

    const baseFloor = Math.max(8, heavyRequirement - dorkOffset - cantripVirtualSource);
    sourcesNeeded[c] = baseFloor;
  }

  // 3. Filter Format & Identity Legal Dual / Rainbow / Fetch Lands
  const normFormat = (format || 'standard').toLowerCase();
  const formatKey = normFormat === 'auto' ? 'standard' : normFormat === 'edh' ? 'commander' : normFormat;

  const legalCatalog = LAND_CATALOG.filter(land => {
    // Check format compatibility
    const isFormatLegal = land.formats.includes(formatKey) || formatKey === 'vintage' || formatKey === 'legacy';
    if (!isFormatLegal) return false;

    // Check color identity compatibility (land colors must be subset of active deck colors)
    const landColors = land.colors;
    if (land.isRainbow) return true; // Rainbow lands legal if format legal
    return landColors.every(c => activeColors.includes(c as typeof colors[number]));
  });

  const suggestedLands: SuggestedLandItem[] = [];
  const sourcesProvided: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };
  let remainingLandSlots = adjustedTargetLands;

  // Single-color Mono deck fast path
  if (activeColors.length === 1) {
    const monoColor = activeColors[0];
    const basicNameMap: Record<string, string> = { W: 'Plains', U: 'Island', B: 'Swamp', R: 'Mountain', G: 'Forest' };
    const basicName = basicNameMap[monoColor];

    suggestedLands.push({
      name: basicName,
      quantity: adjustedTargetLands,
      type: `Basic Land — ${basicName}`,
      colors: [monoColor],
      imageUrl: LAND_IMAGES[basicName],
      isBasic: true,
      isDual: false,
      isRainbow: false,
      isFetch: false,
    });

    sourcesProvided[monoColor] = adjustedTargetLands;

    const advice = generateDynamicManaAdvice(deck, suggestedLands, sourcesNeeded, sourcesProvided, pipStats, adjustedTargetLands, format);
    return {
      targetLands,
      adjustedTargetLands,
      suggestedLands,
      sourcesNeeded,
      sourcesProvided,
      pipStats,
      advice,
    };
  }

  // Multi-color Deck Consolidation Path (Duals, Rainbows, Fetches + Basics)

  // A. Add Rainbow / Utility Lands if available & 2+ colors
  const rainbowLands = legalCatalog.filter(l => l.isRainbow);
  for (const rainbow of rainbowLands) {
    if (remainingLandSlots <= 4) break;
    // Up to 4 copies of rainbow lands like Starting Town / Multiversal Passage / Fabled Passage
    const qty = Math.min(4, Math.floor(remainingLandSlots / 4));
    if (qty > 0) {
      suggestedLands.push({
        name: rainbow.name,
        quantity: qty,
        type: rainbow.type,
        colors: activeColors,
        imageUrl: LAND_IMAGES[rainbow.name] || LAND_IMAGES['Forest'],
        isBasic: false,
        isDual: false,
        isRainbow: rainbow.isRainbow,
        isFetch: rainbow.isFetch,
      });

      remainingLandSlots -= qty;
      for (const c of activeColors) {
        sourcesProvided[c] = (sourcesProvided[c] || 0) + qty;
      }
    }
  }

  // B. Add Color-Pair Dual Lands (Shocklands, Fastlands, Painlands)
  const dualLands = legalCatalog.filter(l => l.isDual);
  for (const dual of dualLands) {
    if (remainingLandSlots <= 2) break;

    // Check if both colors of this dual are in activeColors
    if (dual.colors.every(c => activeColors.includes(c as typeof colors[number]))) {
      // Check if we still need sources for either color
      const needsColor1 = (sourcesProvided[dual.colors[0]] || 0) < (sourcesNeeded[dual.colors[0]] || 0);
      const needsColor2 = (sourcesProvided[dual.colors[1]] || 0) < (sourcesNeeded[dual.colors[1]] || 0);

      if (needsColor1 || needsColor2) {
        const qty = Math.min(4, Math.floor(remainingLandSlots / 2));
        if (qty > 0) {
          suggestedLands.push({
            name: dual.name,
            quantity: qty,
            type: dual.type,
            colors: dual.colors,
            imageUrl: LAND_IMAGES[dual.name] || LAND_IMAGES['Forest'],
            isBasic: false,
            isDual: true,
            isRainbow: false,
            isFetch: false,
          });

          remainingLandSlots -= qty;
          for (const c of dual.colors) {
            sourcesProvided[c] = (sourcesProvided[c] || 0) + qty;
          }
        }
      }
    }
  }

  // C. Fill remaining land slots with Basic Lands split by Pip Ratios
  if (remainingLandSlots > 0 && activeColors.length > 0) {
    const basicNameMap: Record<string, { name: string; type: string }> = {
      W: { name: 'Plains', type: 'Basic Land — Plains' },
      U: { name: 'Island', type: 'Basic Land — Island' },
      B: { name: 'Swamp', type: 'Basic Land — Swamp' },
      R: { name: 'Mountain', type: 'Basic Land — Mountain' },
      G: { name: 'Forest', type: 'Basic Land — Forest' },
    };

    // Calculate basic split based on remaining needed sources and pip ratios
    const activeTotalPips = activeColors.reduce((sum, c) => sum + pipStats.pips[c], 0);

    let allocatedSlots = 0;
    activeColors.forEach((c, idx) => {
      const basicInfo = basicNameMap[c];
      let qty: number;

      if (idx === activeColors.length - 1) {
        // Last color gets all remaining slots
        qty = remainingLandSlots - allocatedSlots;
      } else {
        const ratio = activeTotalPips > 0 ? pipStats.pips[c] / activeTotalPips : 1 / activeColors.length;
        qty = Math.round(remainingLandSlots * ratio);
      }

      if (qty > 0) {
        suggestedLands.push({
          name: basicInfo.name,
          quantity: qty,
          type: basicInfo.type,
          colors: [c],
          imageUrl: LAND_IMAGES[basicInfo.name],
          isBasic: true,
          isDual: false,
          isRainbow: false,
          isFetch: false,
        });

        allocatedSlots += qty;
        sourcesProvided[c] = (sourcesProvided[c] || 0) + qty;
      }
    });
  }

  const advice = generateDynamicManaAdvice(deck, suggestedLands, sourcesNeeded, sourcesProvided, pipStats, adjustedTargetLands, format);

  return {
    targetLands,
    adjustedTargetLands,
    suggestedLands,
    sourcesNeeded,
    sourcesProvided,
    pipStats,
    advice,
  };
}

/**
 * Calculate actual color sources provided by lands currently in the deck list
 */
export function calculateActualLandSources(
  deck: Card[],
  activeColors: string[]
): Record<string, number> {
  const sourcesProvided: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };
  const colors = ['W', 'U', 'B', 'R', 'G'] as const;

  for (const card of deck) {
    if (!card.isLand) continue;
    const qty = card.quantity || 1;
    const nameLower = card.name.toLowerCase();
    const typeLower = (card.type || '').toLowerCase();

    // Basic & Named Type Sources
    if (nameLower.includes('plains') || typeLower.includes('plains')) sourcesProvided.W += qty;
    if (nameLower.includes('island') || typeLower.includes('island')) sourcesProvided.U += qty;
    if (nameLower.includes('swamp') || typeLower.includes('swamp')) sourcesProvided.B += qty;
    if (nameLower.includes('mountain') || typeLower.includes('mountain')) sourcesProvided.R += qty;
    if (nameLower.includes('forest') || typeLower.includes('forest')) sourcesProvided.G += qty;

    // Dual & Rainbow lands by Color Identity / Colors
    const cardColors = (card.colorIdentity && card.colorIdentity.length > 0)
      ? card.colorIdentity
      : card.colors || [];

    const isRainbow = cardColors.length >= 4 ||
      nameLower.includes('starting town') ||
      nameLower.includes('multiversal passage') ||
      nameLower.includes('command tower') ||
      nameLower.includes('mana confluence') ||
      nameLower.includes('city of brass') ||
      nameLower.includes('fabled passage');

    if (isRainbow) {
      for (const c of activeColors) {
        // Avoid double counting if already counted via type (e.g. basic forest)
        if (!nameLower.includes('forest') && !nameLower.includes('mountain') && !nameLower.includes('swamp') && !nameLower.includes('island') && !nameLower.includes('plains')) {
          sourcesProvided[c] = (sourcesProvided[c] || 0) + qty;
        }
      }
    } else {
      for (const c of cardColors) {
        if (colors.includes(c as typeof colors[number])) {
          // Avoid double counting if already counted via type
          const colorTypeNames: Record<string, string> = { W: 'plains', U: 'island', B: 'swamp', R: 'mountain', G: 'forest' };
          if (!nameLower.includes(colorTypeNames[c]) && !typeLower.includes(colorTypeNames[c])) {
            sourcesProvided[c] = (sourcesProvided[c] || 0) + qty;
          }
        }
      }
    }
  }

  return sourcesProvided;
}

/**
 * Generates 100% dynamic, context-only step-by-step math and explanations tailored specifically to active deck
 */
export function generateDynamicManaAdvice(
  deck: Card[],
  suggestedLands: SuggestedLandItem[] = [],
  sourcesNeeded: Record<string, number> = {},
  sourcesProvided: Record<string, number> = {},
  pipStatsInput?: PipCountResult,
  targetLandSlots: number = 24,
  format: string = 'standard'
): string[] {
  const advice: string[] = [];
  const pipStats = pipStatsInput || calculateDeckPipCounts(deck);
  const colors = ['W', 'U', 'B', 'R', 'G'] as const;
  const activeColors = colors.filter(c => pipStats.pips[c] > 0);

  if (activeColors.length === 0) {
    return ["Add non-land spells to your deck to view live mana calculations and land recommendations!"];
  }

  // Calculate actual land sources currently in deck
  const actualLandSources = calculateActualLandSources(deck, activeColors);
  const currentLandCount = deck.filter(c => c.isLand).reduce((sum, c) => sum + c.quantity, 0);

  // Compute needed sources if not passed in
  const computedSourcesNeeded: Record<string, number> = { ...sourcesNeeded };
  for (const c of activeColors) {
    if (!computedSourcesNeeded[c]) {
      const heavyRequirement = pipStats.heaviestPipSpells[c]?.requiredSources || 13;
      const dorkOffset = Math.min(2, pipStats.dorkRockSources[c] || 0);
      const cantripVirtualSource = Math.floor(pipStats.cantripCount / 3);
      computedSourcesNeeded[c] = Math.max(8, heavyRequirement - dorkOffset - cantripVirtualSource);
    }
  }

  // 1. Deck Pip Ratio Summary
  const pipSummary = activeColors.map(c => `{${c}}: ${pipStats.pips[c]}`).join(' | ');
  advice.push(`**Pip Breakdown**: ${pipSummary} (Total Pips: ${pipStats.totalPips})`);

  // 2. Heavy Pip Spells Floor Advice
  activeColors.forEach(c => {
    const heavy = pipStats.heaviestPipSpells[c];
    if (heavy) {
      advice.push(
        `**Heavy Pip Floor ({${c}})**: Card **${heavy.cardName}** (${heavy.cost}) sets your turn ${heavy.cmc} source floor to **${heavy.requiredSources} {${c}} sources**.`
      );
    }
  });

  // 3. Cantrip / Selection Modifier Advice
  if (pipStats.cantripCount > 0) {
    advice.push(
      `**Cantrip & Selection Discount**: Running ${pipStats.cantripCount}x cheap cantrips/selection (${pipStats.cantripNames.slice(0, 3).join(', ')}) adds **+1 effective virtual source** across your colors and allows reducing total land slots by 1.`
    );
  }

  // 4. Turn 1 Untapped Land Requirement Advice
  activeColors.forEach(c => {
    const t1Cards = pipStats.t1OneDrops[c];
    if (t1Cards && t1Cards.length > 0) {
      advice.push(
        `**Turn 1 Untapped Requirement ({${c}})**: You have Turn 1 1-drops (${t1Cards.slice(0, 2).join(', ')}), requiring at least **14 untapped {${c}} sources** on Turn 1 (excluding taplands).`
      );
    }
  });

  // 5. Land Slot Budget Guidance (Current vs Target)
  if (currentLandCount < targetLandSlots) {
    advice.push(
      `**Land Slot Shortage**: You currently have **${currentLandCount} lands** in your deck (${targetLandSlots} recommended). Consider adding **${targetLandSlots - currentLandCount} more land(s)** or click Auto-Suggest Lands.`
    );
  } else if (currentLandCount > targetLandSlots) {
    advice.push(
      `**Land Slot Excess**: You currently have **${currentLandCount} lands** in your deck, exceeding your target of ${targetLandSlots}. Consider cutting **${currentLandCount - targetLandSlots} land(s)** for spells.`
    );
  } else {
    advice.push(`**Land Slot Target Met**: Exactly **${targetLandSlots} lands** in your deck.`);
  }

  // 6. Live Per-Color Source Deficit / Surplus Guidance
  activeColors.forEach(c => {
    const needed = computedSourcesNeeded[c] || 13;
    const provided = actualLandSources[c] || 0;

    if (provided < needed) {
      advice.push(
        `**{${c}} Source Deficit**: You have **${provided} {${c}} sources** in deck, but your spells require at least **${needed} {${c}} sources**. Add **${needed - provided} more {${c}} source(s)** or dual lands.`
      );
    } else if (provided > needed + 4) {
      advice.push(
        `**{${c}} Source Surplus**: You have **${provided} {${c}} sources** in deck, exceeding your required floor of ${needed}. You can safely convert **${provided - needed} {${c}} sources** into utility lands or other colors.`
      );
    } else {
      advice.push(
        `**{${c}} Source Target Met**: **${provided} {${c}} sources** in deck cleanly satisfies your **${needed} required floor**!`
      );
    }
  });

  return advice;
}

